// src/js/utils/profileChartMeta.ts
// Phase 246 Plan 05 — browser-free axis-bounds and inactivity-note logic for the daily-change
// chart on /mc/players/<uuid> (D-04, D-05, D-06, D-07).
//
// This module exists because `src/js/mc/profile.ts` imports the Highcharts package at module scope, so
// `node tests/mc/profile-chart-meta.test.ts` could never load it without a bundler — the same
// reason `src/js/utils/feedTail.ts` was extracted before it.
//
// Hard constraint, stated here rather than only in the pinned test: this file must contain ZERO
// occurrences of the browser's current-instant clock function and ZERO occurrences of the Date
// constructor being invoked. `dataAxisBounds()` takes already-parsed epoch milliseconds from its
// caller (`profile.ts`'s own TZ-safe parseLocalDate()) and never re-derives them;
// `inactivityNoteText()` compares two server-supplied Y-m-d strings, which sort in calendar order
// by plain string comparison, so no date parsing is needed at all. A second client-side notion of
// "today" is the exact TZ-bug class this codebase has repeatedly hardened against (see
// 246-RESEARCH.md's Anti-Patterns section) — the server's own `McAnalyticsModel::torontoToday()`
// (Plan 246-03) is the single source of truth for "today", and this module only ever reads its
// value back out of the payload, never recomputes it.
//
// This module also imports nothing from the Highcharts package and nothing from
// `src/js/mc/profile.ts`, so
// `node` can execute it directly with no build step.
//
// The inactivity note's copy string is defined here, once. `player.latte` renders the
// byte-identical string server-side (Plan 246-04 Task 3, for the first paint before any JS runs);
// the two must never drift — see player.latte §3's own comment pointing back at this file.

/**
 * A CAP (Highcharts' `plotOptions.column.maxPointWidth`), never a fixed width: a well-populated
 * series keeps Highcharts' own responsive column sizing, while a one- or two-point series can no
 * longer expand to occupy half the plot. Resolves 246-RESEARCH.md Open Question 3 without a fixed
 * pixel value that would be wrong at the 320px breakpoint this app is bound to.
 */
export const PROFILE_COLUMN_MAX_POINT_WIDTH = 32;

const HALF_DAY_MS = 43200000;

/**
 * Bounds the daily-change chart's xAxis to the real extremes of its own data, padded by exactly
 * half a day (43200000ms) on each side so a single-point series renders a full day-wide axis
 * instead of collapsing to a near-zero span — the exact defect 246-RESEARCH.md reproduced live
 * (a ~1ms axis span with a column occupying 48% of the plot width).
 *
 * Takes already-parsed epoch milliseconds (the caller's own parseLocalDate() output, never a raw
 * date string and never the browser's current-instant clock). Drops any entry that is not
 * finite — parseLocalDate()
 * returns NaN for a malformed date, and a NaN axis bound would blank the whole chart rather than
 * just degrade one point — and returns null when nothing finite remains.
 *
 * D-05: the bound comes from `dates` alone, never from `window_start`/`window_end` (which equal
 * `today` for four of the five window presets and null for all-time) and never from the browser
 * clock, so the fix behaves identically across all five windows.
 */
export function dataAxisBounds(dates: number[]): { min: number; max: number } | null {
    let min: number | null = null;
    let max: number | null = null;
    for (const d of dates) {
        if (!Number.isFinite(d)) continue;
        if (min === null || d < min) min = d;
        if (max === null || d > max) max = d;
    }
    if (min === null || max === null) return null;
    return { min: min - HALF_DAY_MS, max: max + HALF_DAY_MS };
}

/**
 * D-06/D-07: names the last real day of activity whenever it precedes the server's own Toronto
 * `today`, with no minimum-gap threshold — a one-day gap gets the same note as a forty-day gap.
 *
 * Both arguments are server-supplied Y-m-d strings (`points[].date` and `payload.today` —
 * McAnalyticsModel::torontoToday(), Plan 246-03). They are compared with plain string comparison,
 * never parsed through the Date constructor: Y-m-d strings already sort in calendar order by
 * construction, so no date arithmetic is needed, and none belongs here — see this file's header.
 *
 * Returns null (silence, never a fabricated note) when either argument is missing/empty, when the
 * dates are equal (played today, nothing to report), or when `lastDate` is after `today` (clock
 * skew between the two payload fields) — refused rather than rendered as a negative gap.
 */
export function inactivityNoteText(
    lastDate: string | null | undefined,
    today: string | null | undefined,
): string | null {
    if (!lastDate || !today) return null;
    if (!(lastDate < today)) return null;
    return `No activity since ${lastDate}`;
}
